import { on, run } from '../helper/electron';
import isElectron from '../utils/isElectron';

let cachedBasePath: string | null = null;

export const getAssetBasePath = async (): Promise<string> => {
  if (cachedBasePath) return cachedBasePath;

  if (isElectron() && process.env.NODE_ENV === 'production') {
    run('get-asset-path');

    cachedBasePath = await new Promise<string>((resolve) => {
      on('get-asset-path', (path) => resolve(path));
    });
  } else {
    // Fallback for non-Electron or development mode
    cachedBasePath = '/assets';
  }
  return cachedBasePath;
};

export const getAssetPath = async (relativePath: string) => {
  const normalized = relativePath.replace(/^\/+/, '');
  return toAssetUrl(`${await getAssetBasePath()}/${normalized}`);
};

/** Make a packed resources path usable as img src / fetch URL. */
export const toAssetUrl = (assetPath: string): string => {
  if (
    !assetPath ||
    assetPath.startsWith('http') ||
    assetPath.startsWith('file:') ||
    assetPath.startsWith('/assets')
  ) {
    return assetPath;
  }
  const normalized = encodeURI(assetPath.replace(/\\/g, '/'));
  if (/^[A-Za-z]:\//.test(normalized)) {
    return `file:///${normalized}`;
  }
  if (normalized.startsWith('/')) {
    return `file://${normalized}`;
  }
  return `file:///${normalized}`;
};

export const getIconPath = async (iconName: string) => {
  return getAssetPath(`icons/${iconName}`);
};

export const getFramePath = async (frameFileName: string) => {
  return getAssetPath(`frames/${frameFileName}`);
};

// Export the icons as async getters
export const getIcons = async () => ({
  profileCircle: await getIconPath('profile-circle.svg'),
  playlist: await getIconPath('playlist.svg'),
  arrowCircleLeft: await getIconPath('arrow-circle-left.svg'),
  arrowCircleRight: await getIconPath('arrow-circle-right.svg'),
  arrowLeft: await getIconPath('arrow-left.svg'),
  home: await getIconPath('home.svg'),
  allCrates: await getIconPath('all-crates.svg'),
  charts: await getIconPath('charts.svg'),
  myCrates: await getIconPath('my-crates.svg'),

  genres: await getIconPath('genres.svg'),
  hacks: await getIconPath('hacks.svg'),
  vipCrates: await getIconPath('vip-crates.svg'),
  spotify: await getIconPath('spotify.svg'),
  spotifyIcon: await getIconPath('spotify-icon-green.png'),
  spotifyLogo: await getIconPath('spotify-logo-green.png'),
  textImport: await getIconPath('text-import.svg'),
  seratoIcon: await getIconPath('serato.png'),
  buildFromTrackIcon: await getIconPath('build_from_track.png'),
  shazamToCrate: await getIconPath('shazam-to-crate.svg'),
  folderFlattener: await getIconPath('folder-flattener.svg'),
  marketplace: await getIconPath('text-import.svg'),
  smplLogo: await getIconPath('smpllogo.svg'),
  djepLogo: await getIconPath('djeplogo.svg'),
  futureDJLogo: await getIconPath('future-dj-logo.svg'),
  djStyleWhite: await getIconPath('djstyle_white.svg'),
  djStyleBlack: await getIconPath('djstyle_black.svg'),
  cloudCrates: await getIconPath('cloud-crates.svg'),
  spotifyFull: await getIconPath('spotify-full.svg'),
  apple: await getIconPath('apple.svg'),
  soundcloud: await getIconPath('soundcloud.svg'),
  tidal: await getIconPath('tidal.svg'),
  deezer: await getIconPath('deezer.svg'),
  beatsource: await getIconPath('beatsource.svg'),
  beatport: await getIconPath('beatport.svg'),
  avatar: await getIconPath('avatar.svg'),
  whiteTrash: await getIconPath('white-trash.svg'),
  redTrash: await getIconPath('red-trash.svg'),
  arrowForward: await getIconPath('arrow-forward-outline'),
  arrowBackward: await getIconPath('arrow-back-outline'),
  playButton: await getIconPath('play-button.svg'),
  pauseButton: await getIconPath('pause-button.svg'),
  flameLogo: await getIconPath('icon.png'),
  platterHandOpen: await getIconPath('platter-hand-open.png'),
  platterHandGrab: await getIconPath('platter-hand-grab.png'),
  seratoDJPro: await getIconPath('serato-dj-pro.svg'),
  djayPro: await getIconPath('djay-pro-logo-white.svg'),
  rekordbox: await getIconPath('rekordbox-logo.svg'),
  virtualDJ: await getIconPath('virtual-dj-logo.svg'),
  rotateLeft: await getIconPath('rotate-left.svg'),
  uploadCloud: await getIconPath('upload-cloud.svg'),
  search: await getIconPath('search.svg'),
  plus: await getIconPath('plus.svg'),
  folders: await getIconPath('folders.svg'),
  bangerButtonLogo: await getIconPath('banger-button-logo.svg'),
  magicPlaylistLogo: await getIconPath('magic-playlist-logo.svg'),
  djsVaultLogo: await getIconPath('djs-vault-logo.svg'),
  dangerousDJsLogo: await getIconPath('dangerous-djs-logo.svg'),
  chLogoLightMode: await getIconPath('logo.png'),
  chLogoDarkMode: await getIconPath('logo-dark.svg'),
  favicon: await getIconPath('favicon.png'),
  pdfApple: await getIconPath('pdfCloudCratePNGS/Apple.png'),
  pdfBeatsource: await getIconPath('pdfCloudCratePNGS/Beatsource.png'),
  pdfBeatport: await getIconPath('pdfCloudCratePNGS/Beatport.png'),
  pdfDeezer: await getIconPath('pdfCloudCratePNGS/Deezer.png'),
  pdfSoundcloud: await getIconPath('pdfCloudCratePNGS/Soundcloud.png'),
  pdfSpotify: await getIconPath('pdfCloudCratePNGS/Spotify.png'),
  pdfTidal: await getIconPath('pdfCloudCratePNGS/Tidal.png'),
  pdfCloudCrates: await getIconPath('pdfCloudCratePNGS/CloudCrates.png'),
  algRTHM: await getIconPath('algRTHM.svg'),
});
